/**
 * Client Portal TypeScript interfaces
 */

export interface PortalUser {
  id: string;
  clientId: string;
  practiceId: string;
  email: string;
  firstName: string;
  lastName: string;
  mfaEnabled: boolean;
  authProvider: 'email' | 'magic-link' | 'google';
  lastLoginAt: string | null;
  /** Portal-only display name; when empty, UI uses firstName + lastName */
  displayName?: string | null;
  /** Presigned URL; refresh via GET /profile */
  avatarUrl?: string | null;
}

/** Shown name in header, settings, chat (nickname overrides legal name). */
export function getPortalDisplayName(u: PortalUser | null | undefined): string {
  if (!u) {
    return '';
  }
  const nick = typeof u.displayName === 'string' ? u.displayName.trim() : '';
  if (nick) {
    return nick;
  }
  return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
}

/** First token for greetings, e.g. "Hi, Jane" */
export function getPortalGreetingName(u: PortalUser | null | undefined): string {
  const full = getPortalDisplayName(u);
  if (full) {
    return full.split(/\s+/)[0] ?? full;
  }
  return 'there';
}

export function getPortalInitials(u: PortalUser | null | undefined): string {
  const name = getPortalDisplayName(u);
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? '';
    const b = parts[parts.length - 1]?.[0] ?? '';
    const s = `${a}${b}`.toUpperCase();
    return s ?? '?';
  }
  return name.slice(0, 2).toUpperCase() ?? '?';
}

export interface TaxReturn {
  id: string;
  year: number;
  type: string;
  status: string;
  dueDate: string | null;
  lodgementDate: string | null;
  refundAmount: number | null;
}

export interface Deadline {
  id: string;
  type: string;
  dueDate: string;
  description: string;
  isOverdue: boolean;
  daysRemaining: number;
}

export type ChatRoomType = 'accountant' | 'ai';

export interface ChatRoom {
  id: string;
  title: string;
  status: string;
  roomType: ChatRoomType;
  lastMessageAt: string;
  lastMessage?: string;
  unreadCount: number;
  practiceId: string;
}

export interface ChatMessage {
  id: string;
  chatRoomId: string;
  senderType: 'client' | 'staff' | 'system' | 'ai';
  senderId: string;
  content: string;
  contentType: 'text' | 'file' | 'system';
  fileId?: string;
  metadata?: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface PortalNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  channel: string;
  status: string;
  readAt: string | null;
  createdAt: string;
}

export interface PortalDocument {
  id: string;
  name: string;
  category: string;
  fileType: string;
  fileSize: number;
  mimeType: string;
  visibility: string;
  uploadedBy: string;
  createdAt: string;
}

export interface OutstandingItem {
  id: string;
  type: string;
  description: string;
  amount?: number;
  dueDate?: string;
  isOverdue: boolean;
}

export interface ActionItem {
  id: string;
  type: string;
  title: string;
  description: string;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  link?: string;
}

export interface DashboardSummary {
  taxReturns: TaxReturn[];
  deadlines: Deadline[];
  outstandingItems: OutstandingItem[];
  actionRequired: ActionItem[];
}

export interface IncomeTaxAccount {
  balance: number;
  lastUpdated: string;
  transactions: Transaction[];
}

export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  runningBalance: number;
  type: 'debit' | 'credit';
}

export interface DocumentRequest {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'uploaded' | 'accepted';
  dueDate?: string;
}

export interface DashboardDeadlinesResponse {
  deadlines: Deadline[];
}

export interface DashboardTaxReturnsResponse {
  taxReturns: TaxReturn[];
}

export interface PracticeInfo {
  id: string;
  name: string;
  logo?: string;
  themeColor?: string;
}

// ── Reconciliation Types ─────────────────────────────────────────────
export interface ShareLink {
  token: string;
  clientId: string;
  clientName: string;
  status: string;
  expiresAt: string | null;
  reviewSubmittedAt: string | null;
  type?: string;
  periodFilter?: string;
}

export interface ShareTransaction {
  _id: string;
  account: string;
  source_file: string;
  date: string;
  narrative: string;
  amount: number;
  comment: string;
  comment_modified_by: string;
  gst_flag: boolean;
  gst_amt: number;
  gst_flag_manual_override: boolean;
  biz_percent: number;
}

export interface TransactionUpdate {
  comment?: string;
  gst_flag?: boolean;
  gst_amt?: number;
  biz_percent?: number;
}

// ── Bookkeeping Types ────────────────────────────────────────────────
export interface BookkeepingTransaction {
  _id: string;
  account: string;
  date: string;
  narrative: string;
  amount: number;
  source_file?: string;
  gst_flag?: boolean;
  gst_amt?: number;
  reconciled?: boolean;
}

export interface TransactionsSummary {
  total: number;
  reconciled: number;
  unreconciled: number;
}

export interface JournalEntry {
  _id: string;
  date: string;
  reference: string;
  description: string;
  entries: { account: string; debit: number; credit: number }[];
}

export interface ChartOfAccountsItem {
  code: string;
  name: string;
  type: string;
  balance?: number;
}

export interface FinancialReport {
  title: string;
  asOfDate?: string;
  period?: string;
  sections: ReportSection[];
}

export interface ReportSection {
  name: string;
  items: ReportLineItem[];
  total: number;
}

export interface ReportLineItem {
  account: string;
  name: string;
  amount: number;
}

// ── Payroll Types ────────────────────────────────────────────────────
export interface PayrollStatus {
  hasPayroll: boolean;
  employeeId?: string;
  onboardingStatus?: string;
  available?: boolean;
  message?: string;
}

export interface OnboardingStatus {
  status: 'pending' | 'completed' | 'not_applicable';
  onboardingUrl?: string;
}

export interface PaySlip {
  id: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  payDate: string;
  grossPay: number;
  netPay: number;
  tax: number;
  super: number;
}

export interface LeaveBalance {
  annualLeave: { balance: number; accrued: number; taken: number };
  sickLeave: { balance: number; accrued: number; taken: number };
  longServiceLeave?: { balance: number };
}
